import React, { useState } from "react";
import apiService from "../../services/api";
import InnerLayout from "../../layouts/InnerLayout";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  FormHelperText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import InnerBanner from "../../images/inner-banner.jpg";
import CallforAction from "../../components/call_for_action/CallForActtion.js";
import TermsConditions from "../puja_request/TermsConditions.js";
import { useAppSettings } from "../../contexts/AppSettingsContext";

const validationSchema = yup.object({
  request_id: yup.string().required("Request ID is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  name: yup.string().required("Requestor Name is required"),
});

const changeRequestSchema = yup.object({
  name: yup.string().required("Requestor Name is required"),
  new_event_date: yup.string().required("New Event Date is required"),
  time_of_event: yup.string().required("Time of Event is required"),
  comments: yup.string().required("Comments are required").min(10, "Comments must be at least 10 characters long"),
  terms_accepted: yup.boolean().oneOf([true], "You must accept the terms."),
});

function ChangeHallBooking() {
  const [bookingDetails, setBookingDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { changeAndCancelNote } = useAppSettings() || {};

  // Form Hooks
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(validationSchema) });

  const {
    register: registerChange,
    handleSubmit: handleSubmitChange,
    formState: { errors: errorsChange },
  } = useForm({ resolver: yupResolver(changeRequestSchema) });

  // API Call to Check Hall Booking
  const handleSearch = async (data) => {
    setLoading(true); // Start loading
    try {
      const response = await apiService.getHallBookingDetails(data);
      if (response.data.success) {
        // Verify if the provided name matches the booking name
        if (response.data.data.name.toLowerCase() === data.name.toLowerCase()) {
          setBookingDetails(response.data.data);
          setErrorMessage("");
        } else {
          setErrorMessage("The provided name does not match the booking name.");
          setBookingDetails(null);
        }
      } else {
        setErrorMessage(
          "No request found with the provided Request ID and Email and name."
        );
        setBookingDetails(null);
      }
    } catch (error) {
      setErrorMessage("Error fetching booking details. Please try again.");
      setBookingDetails(null);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  console.log("booking details are:", bookingDetails);

  // API Call to Submit Change Request
  const handleChangeRequest = async (data) => {
    setLoading(true); // Start loading
    try {
      const payload = {
        request_id: bookingDetails.hall_request_id,
        name: data.name,
        email: bookingDetails.email,
        new_event_date: data.new_event_date,
        time_of_event: data.time_of_event,
        comments: data.comments,
        terms_conditions: data.terms_conditions,
      };
      console.log("form data is :", payload);
      const response = await apiService.createChangeHallRequest(payload);

      if (response.data.success) {
        sessionStorage.setItem(
          "successPageState",
          JSON.stringify({
            type: "change-social-hall",
            requestId: response.data.data.hall_request_id,
          })
        );
        navigate("/success", {
          state: {
            type: "change-social-hall",
            requestId: response.data.data.hall_request_id,
          },
        });
      } else {
        setErrorMessage("Error processing request. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting change request:", error);
      setErrorMessage("Error processing request. Please try again.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <InnerLayout>
      <Box component="section" className="inner-banner" sx={{ p: 0 }}>
        <Box component="img" className="banner-pic" src={InnerBanner} />
        <Box className="inner-text">
          <Container maxWidth="xl">
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid item size={{ md: 6 }}>
                <Typography
                  component="h1"
                  variant="h1"
                  className="section-title text-white"
                >
                  Change Hall Booking
                </Typography>
              </Grid>
              <Grid item size={{ md: 6 }}>
                <CallforAction />
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      <Box component="section">
        <Container maxWidth="xl">
          <Box className="custom-card" sx={{ mx: "auto" }}>
            {/* Show change/cancel note above both forms, support HTML */}
            {changeAndCancelNote && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body1"
                  color="info.main"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: changeAndCancelNote }}
                />
              </Box>
            )}
            {!bookingDetails ? (
              <form onSubmit={handleSubmit(handleSearch)}>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Request ID *"
                      variant="outlined"
                      fullWidth
                      {...register("request_id")}
                      error={!!errors.request_id}
                      helperText={errors.request_id?.message}
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Email *"
                      variant="outlined"
                      fullWidth
                      {...register("email")}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Requestor Name *"
                      variant="outlined"
                      fullWidth
                      {...register("name")}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 12 }}>
                    {errorMessage && (
                      <Typography color="error">{errorMessage}</Typography>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : "Search"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Box>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
                    <Typography
                      variant="h6"
                      className="text-primary font-700 border-title"
                    >
                      Change Hall Booking Form
                    </Typography>

                    {/* Change Request Form */}
                    <form onSubmit={handleSubmitChange(handleChangeRequest)}>
                      <Grid container spacing={2}>
                        <Grid item size={{ xs: 12, md: 12 }}>
                          <TextField
                            label="Requestor Name *"
                            variant="outlined"
                            fullWidth
                            {...registerChange("name")}
                            defaultValue={bookingDetails?.name || ""}
                            disabled={!!bookingDetails}
                            error={!!errorsChange.name}
                            helperText={errorsChange.name?.message}
                            sx={{ mt: 3 }}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                          <TextField
                            type="date"
                            label="New Event Date *"
                            variant="outlined"
                            fullWidth
                            {...registerChange("new_event_date")}
                            error={!!errorsChange.new_event_date}
                            helperText={errorsChange.new_event_date?.message}
                            sx={{ mt: 3 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                          <FormControl
                            fullWidth
                            sx={{ mt: 3 }}
                            error={!!errorsChange.time_of_event}
                          >
                            <InputLabel>Time of Event *</InputLabel>
                            <Select {...registerChange("time_of_event")}>
                              {Array.from({ length: 15 }, (_, index) => {
                                const hour = index + 5;
                                const period = hour < 12 ? "AM" : "PM";
                                const formattedHour =
                                  hour > 12 ? hour - 12 : hour;
                                return (
                                  <MenuItem key={hour} value={`${hour}:00`}>
                                    {`${formattedHour}:00 ${period}`}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            {errorsChange.time_of_event && (
                              <FormHelperText>
                                {errorsChange.time_of_event.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item size={{ xs: 12, md: 12 }}>
                          <TextField
                            label="Comments *"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            {...registerChange("comments")}
                            error={!!errorsChange.comments}
                            helperText={errorsChange.comments?.message}
                            sx={{ mt: 3 }}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                          <TermsConditions type="socialhall" />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 12 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ mt: 3 }}
                            disabled={loading}
                          >
                            {loading ? (
                              <CircularProgress size={24} />
                            ) : (
                              "Submit Request"
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
                    <Box className="bg-light p-30">
                      <Typography
                        variant="h6"
                        className="text-primary font-700 border-title"
                      >
                        Booking Details
                      </Typography>

                      <Box className="address-section">
                        <Typography>
                          <Typography component="span">Name:</Typography>{" "}
                          {bookingDetails.name}
                        </Typography>
                        <Typography>
                          <Typography component="span">Request Id:</Typography>{" "}
                          {bookingDetails.hall_request_id}
                        </Typography>
                        <Typography>
                          <Typography component="span">Email:</Typography>{" "}
                          {bookingDetails.email}
                        </Typography>
                        <Typography>
                          <Typography component="span">
                            Date of Event:
                          </Typography>{" "}
                          {bookingDetails.date_of_event}
                        </Typography>
                        <Typography>
                          <Typography component="span">
                            Alternate Date:
                          </Typography>{" "}
                          {bookingDetails.alternate_date_of_event}
                        </Typography>
                      </Box>

                      <Typography
                        variant="p"
                        component="p"
                        className="text-primary font-700 border-title"
                      >
                        Selected Halls:
                      </Typography>

                      {bookingDetails.selected_halls.map((hall) => {
                        // Calculate total: hall cost + sum of addon costs
                        const hallTotal =
                          hall.hall_cost +
                          (hall.addons?.reduce(
                            (sum, addon) => sum + addon.addon_cost,
                            0
                          ) || 0);

                        return (
                          <Box className="hall-list-item" key={hall.id}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              {hall.name}
                              <Typography
                                variant="body2"
                                className="font-700 text-primary"
                              >
                                ${hall.hall_cost}
                              </Typography>
                            </Box>

                            {hall.addons.length > 0 && (
                              <Box className="addon-list p-0" sx={{ mt: 1 }}>
                                <Typography
                                  variant="subtitle1"
                                  className="font-700"
                                >
                                  Add-ons:
                                </Typography>
                                {hall.addons.map((addon) => (
                                  <Box
                                    className="addon-list-item"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    key={addon.id}
                                  >
                                    {addon.name}
                                    <Typography
                                      className="font-700 text-primary"
                                      variant="body2"
                                    >
                                      ${addon.addon_cost}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mt: 1 }}
                            >
                              <Typography
                                variant="body1"
                                className="font-700 text-primary"
                              >
                                Total:
                              </Typography>
                              <Typography
                                variant="body1"
                                className="font-700 text-primary"
                              >
                                ${hallTotal.toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </InnerLayout>
  );
}

export default ChangeHallBooking;